import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthShell, AUTH_TITLE_CLASS } from '@/components/auth/AuthShell';
import { cn } from '@/lib/utils';
import { welcomeApi, type WelcomePreview } from '../api/services';
import { useAuth } from './AuthProvider';
import MfaChallengePage from './MfaChallengePage';
import { WELCOME_SIGN_IN_STEP } from './welcomeFlags';

// /welcome#<token>: the link in the welcome email for an account someone else
// created (an admin's invite, a platform-created organisation, a paid
// sign-up). It shows who it's for and spends nothing until Continue, so an
// email scanner that opens links can't use it up. Continue signs the person
// in once and starts their setup, whose first step asks how they'll sign in
// next time (OnboardingWizardPage).
//
// The token rides in the fragment, which browsers never send to a server. It
// leaves the address bar as soon as it's read, and is kept in this tab's
// session storage so a refresh still works, until it's used.

const TOKEN_KEY = 'cadence_welcome_token';

function readToken(): string {
  try {
    const fromHash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (fromHash) {
      sessionStorage.setItem(TOKEN_KEY, fromHash);
      return fromHash;
    }
    return sessionStorage.getItem(TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

function forget(): void {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* storage unavailable */ }
}

type Problem = { message: string; signInUrl?: string } | 'unreachable' | 'busy';

/** Refusals that mean the link itself is done with; anything else may work on a retry. */
const FINAL_CODES = new Set(['invite_invalid', 'invite_used', 'invite_expired', 'invite_replaced', 'already_signed_in', 'sso_required']);

export default function WelcomePage() {
  const [token] = useState(readToken);
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname);
    const before = document.title;
    document.title = 'Welcome · Cadence';
    return () => { document.title = before; };
  }, []);
  const navigate = useNavigate();
  const { applyTokens, beginMfaChallenge, requiresMfa, isAuthenticated } = useAuth();
  const [preview, setPreview] = useState<WelcomePreview | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [continuing, setContinuing] = useState(false);
  const [awaitingCode, setAwaitingCode] = useState(false);

  const refuse = useCallback((err: any) => {
    const data = err?.response?.data;
    if (!err?.response) { setProblem('unreachable'); return; }
    // Busy (the sign-in limit) or a server error: the link still works, so keep it.
    if (!FINAL_CODES.has(data?.code)) { setProblem('busy'); return; }
    // A link that's spent or gone won't come back: stop keeping it.
    forget();
    setProblem({
      message: data?.error || 'This link doesn’t work. Ask whoever invited you to send it again.',
      signInUrl: data?.code === 'sso_required' ? data?.signInUrl : undefined,
    });
  }, []);

  const check = useCallback(() => {
    if (!token) {
      setProblem({ message: 'This link is missing its code. Open it again from your welcome email.' });
      return;
    }
    setProblem(null);
    welcomeApi.preview(token).then(setPreview).catch(refuse);
  }, [token, refuse]);
  useEffect(check, [check]);

  // Signed in after the two-step code: on into setup.
  useEffect(() => {
    if (awaitingCode && isAuthenticated) navigate('/', { replace: true });
  }, [awaitingCode, isAuthenticated, navigate]);

  const begin = async () => {
    if (continuing) return;
    setContinuing(true);
    try {
      const data = await welcomeApi.accept(token);
      forget();
      // Setup asks how they'll sign in next time first (they have no way yet).
      try { sessionStorage.setItem(WELCOME_SIGN_IN_STEP, '1'); } catch { /* storage unavailable */ }
      if (data.requiresMfa && data.mfaToken) {
        setAwaitingCode(true);
        beginMfaChallenge(data.mfaToken);
        return;
      }
      applyTokens(data);
      if (data.mfaRequired) {
        // An organisation's admin sets up two-step sign-in before anything else.
        sessionStorage.setItem('mfaRequired', 'true');
        navigate('/mfa-setup', { replace: true });
      } else {
        // Home sends anyone who hasn't finished setup into it.
        navigate('/', { replace: true });
      }
    } catch (err) {
      refuse(err);
      setContinuing(false);
    }
  };

  if (awaitingCode && requiresMfa) {
    return (
      <AuthShell width="form">
        <MfaChallengePage />
      </AuthShell>
    );
  }

  return (
    <AuthShell width="form">
      <main className="flex flex-col">
        <div className="mb-6"><BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" /></div>

        {problem === 'unreachable' || problem === 'busy' ? (
          <>
            <h1 className={cn('mb-3', AUTH_TITLE_CLASS)}>Welcome to Cadence</h1>
            <Alert variant="warning"><AlertDescription>
              {problem === 'busy'
                ? 'Cadence is busy right now. Your link still works: try again in a minute.'
                : 'We couldn’t reach Cadence. Check your connection and try again.'}
            </AlertDescription></Alert>
            <Button variant="outline" className="mt-4 self-start" onClick={check}>Try again</Button>
          </>
        ) : problem ? (
          <>
            <h1 className={cn('mb-3', AUTH_TITLE_CLASS)}>Welcome to Cadence</h1>
            <Alert variant="warning"><AlertDescription>{problem.message}</AlertDescription></Alert>
            {problem.signInUrl ? (
              <Button asChild className="mt-4 self-start"><a href={problem.signInUrl}>Sign in with your organisation</a></Button>
            ) : (
              <Button asChild variant="outline" className="mt-4 self-start"><a href="/">Go to sign in</a></Button>
            )}
          </>
        ) : !preview ? (
          <>
            <h1 className="sr-only">Welcome to Cadence</h1>
            <Loader2 className="h-6 w-6 motion-safe:animate-spin text-muted-foreground" aria-label="Loading" />
          </>
        ) : (
          <>
            <p className="text-xs font-medium uppercase tracking-[0.07em] text-muted-foreground">Welcome</p>
            <h1 className={cn('mb-2 mt-1', AUTH_TITLE_CLASS)}>
              Hi {preview.firstName}, continue to set up Cadence
            </h1>
            <p className="mb-2 text-base text-foreground/70">
              {preview.organisationName ? <>Your account for <strong className="text-foreground">{preview.organisationName}</strong> is ready. </> : 'Your account is ready. '}
              Setup takes a few minutes, starting with how you’ll sign in next time.
            </p>
            <p className="mb-6 text-sm text-muted-foreground">
              This is the account for <strong className="break-all text-foreground/90">{preview.email}</strong>. If that isn’t you, close this page.
            </p>
            <Button onClick={begin} disabled={continuing} className="gap-2 self-start">
              {continuing ? <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden /> : <ArrowRight className="h-4 w-4" aria-hidden />}
              Continue
            </Button>
            <p className="mt-4 text-xs text-muted-foreground">This link signs you in once.</p>
          </>
        )}
      </main>
    </AuthShell>
  );
}
